import { useContext, useEffect, useRef, useState } from "react";
import { PlusCircle } from "react-feather";
import DataTable from "../Generic/DataTable";
import Dropdown from "../Generic/DropDown";
import Loading from "../Generic/loading";
import NumberBox from "../Generic/NumberBox";
import TextBox from "../Generic/TextBox";
import { tenantContext } from "../Main/contexts";
import {
    DeleteTenant,
    GetConnections,
    GetOrganizations,
    GetTenant,
    GetTenants,
    PutTenant,
    THEMES,
    TenantItem,
} from "./ConfigAPI";
import {
    FLAG_OPTIONS,
    FieldErrors,
    FormState,
    NO_ORGANIZATION,
    Option,
    TENANT_FIELDS,
    choiceOptions,
    codeProblem,
    deleteConfirmText,
    displayName,
    errorSummary,
    failureMessage,
    fieldErrorsFromResponse,
    formFromSettings,
    formatModified,
    organizationsOf,
    tenantOrganization,
    tenantRequest,
    tenantRows,
} from "./configForm";
import EffectiveValue from "./EffectiveValue";
import { EDITOR_TITLE_STYLE, HEADER_BUTTON_STYLE, Section, column } from "./layout";
import "./styles.css";

const LABELS: Record<string, string> = {
    code: "Code",
    name: "Name",
    organization: "Organization",
    disabled: "Disabled",
    theme: "Theme",
    enableCloudMailProcessing: "Enable cloud mail processing",
    autoResponseMaxPerWindow: "Max per window",
    autoResponseWindowMinutes: "Window minutes",
    autoResponseBodySizeToleranceBytes: "Body-size tolerance bytes",
    connection: "Connection",
    database: "Database",
    useRdsProxy: "Use RDS proxy",
    s3CachePrefix: "S3 cache prefix",
    sqsQueueUrl: "SQS queue URL",
    sqsRegion: "SQS region",
};

/** The list to choose from, or just the stored value while the list has not loaded. */
function listOrStored(list: readonly string[] | undefined, value: string | undefined): readonly string[] {
    return list ?? (value ? [value] : []);
}

/** A disabled tenant's row is greyed out rather than given its own column. It stays clickable. */
function rowClass(row: TenantItem): string {
    return (row.effective?.disabled ?? row.stored?.disabled) === true ? "cc-disabled-row" : "";
}

const COLUMNS = [
    column<TenantItem>("organization", "Organization", "30%", row => tenantOrganization(row) || NO_ORGANIZATION),
    column<TenantItem>("code", "Code", "30%", row => row.code),
    column<TenantItem>("name", "Name", "40%", row => displayName(row)),
];

/**
 * Tenants in the Core configuration database (ADO 10076). The same layout and
 * rules as the Organizations page; tenants have no secrets. The Organization
 * and Connection dropdowns are filled from their own endpoints. If one of them
 * fails, the dropdown offers only Not set and the stored value.
 */
function Tenants() {
    const { selectedTenant } = useContext(tenantContext);
    const token = selectedTenant?.token;

    const [items, setItems] = useState<TenantItem[]>([]);
    const [organizations, setOrganizations] = useState<string[] | undefined>([]);
    const [connections, setConnections] = useState<string[] | undefined>([]);
    const [listState, setListState] = useState<"loading" | "loaded" | "failed">("loading");
    const [listError, setListError] = useState("");
    const [filter, setFilter] = useState("");
    const [organizationFilter, setOrganizationFilter] = useState("");
    const [selectedCode, setSelectedCode] = useState("");

    const [isNew, setIsNew] = useState<boolean | undefined>(undefined);
    const [item, setItem] = useState<TenantItem | undefined>(undefined);
    const [code, setCode] = useState("");
    const [form, setForm] = useState<FormState>({});
    const [errors, setErrors] = useState<FieldErrors>({});
    const [savedJson, setSavedJson] = useState("");
    const [recordLoading, setRecordLoading] = useState(false);
    // The code of the latest open(), so a slow earlier response cannot overwrite a later one.
    const latestOpen = useRef("");

    useEffect(() => {
        loadList();
    }, [token]);

    async function loadList() {
        if (!token) {
            return;
        }
        setListState("loading");
        const [tenantResult, organizationResult, connectionResult] = await Promise.all([
            GetTenants(token),
            GetOrganizations(token),
            GetConnections(token),
        ]);
        setOrganizations(
            organizationResult.isOk && organizationResult.data ? organizationResult.data.map(o => o.code) : undefined,
        );
        setConnections(connectionResult.isOk && connectionResult.data ? connectionResult.data : undefined);
        if (tenantResult.isOk && tenantResult.data) {
            setItems(tenantResult.data);
            // A filter on an organization that no longer has tenants would match nothing and could not be cleared.
            const remaining = organizationsOf(tenantResult.data).map(o => o.toLowerCase());
            setOrganizationFilter(current =>
                current === "" || current === NO_ORGANIZATION || remaining.includes(current.toLowerCase())
                    ? current
                    : "",
            );
            setListState("loaded");
        } else {
            setListError(failureMessage(tenantResult.status));
            setListState("failed");
        }
    }

    function edit(next: TenantItem | undefined, newRecord: boolean) {
        const nextCode = next?.code ?? "";
        const nextForm = formFromSettings(TENANT_FIELDS, next?.stored);
        setItem(next);
        setCode(nextCode);
        setForm(nextForm);
        setErrors({});
        setSavedJson(JSON.stringify({ code: nextCode, form: nextForm }));
        setIsNew(newRecord);
    }

    function confirmDiscard(): boolean {
        const unsaved = isNew !== undefined && JSON.stringify({ code, form }) !== savedJson;
        return !unsaved || window.confirm("Discard your unsaved changes?");
    }

    async function open(rowCode: string) {
        if (!token || !confirmDiscard()) {
            return;
        }
        latestOpen.current = rowCode;
        setRecordLoading(true);
        const result = await GetTenant(token, rowCode);
        if (latestOpen.current !== rowCode) {
            return;
        }
        setRecordLoading(false);
        if (result.isOk && result.data) {
            // Selected only now, so a failed open leaves the list matching the editor.
            setSelectedCode(rowCode);
            edit(result.data, false);
        } else if (result.status === 404) {
            alert(`${rowCode} no longer exists.`);
            setIsNew(undefined);
            setSelectedCode("");
            await loadList();
        } else {
            alert(failureMessage(result.status));
        }
    }

    function addNew() {
        if (!confirmDiscard()) {
            return;
        }
        latestOpen.current = "";
        setRecordLoading(false);
        setSelectedCode("");
        edit(undefined, true);
    }

    function updateValue(field: string, value: string) {
        if (field === "code") {
            setCode(value);
        } else {
            setForm(current => ({ ...current, [field]: value }));
        }
    }

    async function save() {
        if (!token) {
            return;
        }
        const { body, errors: localErrors } = tenantRequest(form);
        if (isNew) {
            const problem = codeProblem(
                code,
                items.map(i => i.code),
            );
            if (problem) {
                localErrors.code = problem;
            }
        }
        if (Object.keys(localErrors).length > 0) {
            setErrors(localErrors);
            alert(errorSummary(localErrors, LABELS));
            return;
        }

        const result = await PutTenant(token, code, body);
        if (result.isOk && result.data) {
            edit(result.data, false);
            setSelectedCode(result.data.code);
            alert("success");
            await loadList();
        } else if (result.status === 400) {
            const serverErrors = fieldErrorsFromResponse(result);
            setErrors(serverErrors);
            const problem = result.validationErrors as unknown as { detail?: string; title?: string } | undefined;
            alert(
                errorSummary(serverErrors, LABELS) ||
                    problem?.detail ||
                    problem?.title ||
                    "The server rejected the save.",
            );
        } else {
            alert(failureMessage(result.status));
        }
    }

    async function remove() {
        if (!token || !item || !window.confirm(deleteConfirmText(item.code))) {
            return;
        }
        const result = await DeleteTenant(token, item.code);
        if (result.isOk) {
            setIsNew(undefined);
            setItem(undefined);
            setSelectedCode("");
            alert("deleted");
            await loadList();
        } else {
            alert(failureMessage(result.status));
        }
    }

    function copyEffective() {
        if (item?.effective) {
            setForm(formFromSettings(TENANT_FIELDS, item.effective));
            alert("Copied. Nothing is saved until Create or Update.");
        }
    }

    const stored = item?.stored;
    const effective = item?.effective;

    function textRow(path: string) {
        return (
            <div
                className="formRow"
                key={path}
            >
                <TextBox
                    id={path}
                    label={LABELS[path]}
                    value={form[path]}
                    onChange={updateValue}
                    maxLength={500}
                    errorMessage={errors[path] ?? ""}
                ></TextBox>
                <EffectiveValue
                    stored={stored}
                    effective={effective}
                    path={path}
                    error={errors[path]}
                />
            </div>
        );
    }

    function numberRow(path: string) {
        return (
            <div
                className="formRow"
                key={path}
            >
                <NumberBox
                    id={path}
                    label={LABELS[path]}
                    value={form[path]}
                    onChange={updateValue}
                    maxLength={10}
                    errorMessage={errors[path] ?? ""}
                ></NumberBox>
                <EffectiveValue
                    stored={stored}
                    effective={effective}
                    path={path}
                    error={errors[path]}
                />
            </div>
        );
    }

    function choiceRow(path: string, options: Option[]) {
        return (
            <div
                className="formRow"
                key={path}
            >
                <Dropdown
                    name={path}
                    label={LABELS[path]}
                    value={form[path] ?? ""}
                    returnFunc={updateValue}
                    Valid={!errors[path]}
                    options={options}
                ></Dropdown>
                <EffectiveValue
                    stored={stored}
                    effective={effective}
                    path={path}
                    error={errors[path]}
                />
            </div>
        );
    }

    const rows = tenantRows(items, filter, organizationFilter);
    const organizationChoices = organizationsOf(items);

    return (
        <>
            <div className="topSection">
                <div className="sectionHeader">
                    {" "}
                    {listState === "loaded" && (
                        <PlusCircle
                            className="headerIcon"
                            aria-label="New tenant"
                            onClick={addNew}
                        ></PlusCircle>
                    )}
                    Tenants{" "}
                </div>
                {isNew !== undefined && !recordLoading && (
                    <div>
                        {!isNew && stored && (
                            <button
                                className="g-btn-large btn-warning"
                                style={HEADER_BUTTON_STYLE}
                                onClick={remove}
                            >
                                Delete
                            </button>
                        )}
                        <button
                            className="g-btn-large btn-primary"
                            style={HEADER_BUTTON_STYLE}
                            onClick={save}
                        >
                            {isNew ? "Create Tenant" : "Update Tenant"}
                        </button>
                    </div>
                )}
            </div>
            <div style={{ display: "flex", flexDirection: "row", height: "calc(100% - 51px)" }}>
                <div className="Users-bodyCol1">
                    {listState === "loading" && <Loading></Loading>}
                    {listState === "failed" && <div className="cc-message">{listError}</div>}
                    {listState === "loaded" && (
                        <>
                            <div className="filterContainer">
                                <div>Filter: </div>
                                <div>
                                    <input
                                        type="text"
                                        aria-label="Filter tenants"
                                        style={{ width: "100%" }}
                                        onChange={e => setFilter(e.currentTarget.value)}
                                        value={filter}
                                    />
                                </div>
                            </div>
                            <div className="filterContainer">
                                <div>Org: </div>
                                <div>
                                    <select
                                        aria-label="Filter by organization"
                                        style={{ width: "100%" }}
                                        value={organizationFilter}
                                        onChange={e => setOrganizationFilter(e.currentTarget.value)}
                                    >
                                        <option value="">All organizations</option>
                                        {organizationChoices.map(choice => (
                                            <option
                                                key={choice}
                                                value={choice}
                                            >
                                                {choice}
                                            </option>
                                        ))}
                                        <option value={NO_ORGANIZATION}>{NO_ORGANIZATION}</option>
                                    </select>
                                </div>
                            </div>
                            {rows.length === 0 && <div className="cc-message">No tenants.</div>}
                            <DataTable
                                data={rows}
                                columns={COLUMNS}
                                onRowClick={(val: string) => {
                                    open(val);
                                }}
                                onRowDblClick={() => {}}
                                selectedItem={selectedCode}
                                uniqueIdentifiyer={x => x.code}
                                getCustomClass={rowClass}
                            ></DataTable>
                        </>
                    )}
                </div>
                {(isNew !== undefined || recordLoading) && (
                    <div className="bodyCol2">
                        {recordLoading && <Loading></Loading>}
                        {!recordLoading && isNew !== undefined && (
                            <>
                                <div className="optionsRow">
                                    <p style={EDITOR_TITLE_STYLE}>
                                        {isNew ? "Create Tenant" : `Edit Tenant ${item?.code}`}
                                    </p>
                                </div>
                                <div style={{ padding: "5px 0px 0px 5px" }}>
                                    {item && !effective && (
                                        <div className="cc-message">
                                            Saved in the database. This API host has not applied it yet.
                                        </div>
                                    )}
                                    {item && !stored && effective && (
                                        <div className="cc-message">
                                            No database values yet. Every value in use comes from appsettings.{" "}
                                            <button
                                                type="button"
                                                className="g-btn btn-default"
                                                onClick={copyEffective}
                                            >
                                                Copy effective values into the form
                                            </button>
                                        </div>
                                    )}
                                    {stored?.modifiedAt && (
                                        <div className="cc-message">Last saved {formatModified(stored)}</div>
                                    )}
                                    {organizations === undefined && (
                                        <div className="cc-message">The organization list could not be loaded.</div>
                                    )}
                                    {connections === undefined && (
                                        <div className="cc-message">The connection list could not be loaded.</div>
                                    )}
                                    <Section title="General">
                                        <div className="formRow">
                                            <TextBox
                                                id="code"
                                                label="Code"
                                                value={code}
                                                onChange={updateValue}
                                                maxLength={100}
                                                disabled={!isNew}
                                                requiredIdentifier={isNew}
                                                errorMessage={errors.code ?? ""}
                                            ></TextBox>
                                            <EffectiveValue error={errors.code} />
                                        </div>
                                        {textRow("name")}
                                        {choiceRow(
                                            "organization",
                                            choiceOptions(
                                                listOrStored(organizations, form.organization),
                                                form.organization ?? "",
                                            ),
                                        )}
                                        {choiceRow("disabled", FLAG_OPTIONS)}
                                        {choiceRow("theme", choiceOptions(THEMES, form.theme ?? ""))}
                                    </Section>
                                    <Section title="Mail processing">
                                        {choiceRow("enableCloudMailProcessing", FLAG_OPTIONS)}
                                        {numberRow("autoResponseMaxPerWindow")}
                                        {numberRow("autoResponseWindowMinutes")}
                                        {numberRow("autoResponseBodySizeToleranceBytes")}
                                    </Section>
                                    <Section title="Database">
                                        {choiceRow(
                                            "connection",
                                            choiceOptions(
                                                listOrStored(connections, form.connection),
                                                form.connection ?? "",
                                            ),
                                        )}
                                        {textRow("database")}
                                        {choiceRow("useRdsProxy", FLAG_OPTIONS)}
                                    </Section>
                                    <Section title="AWS">
                                        {textRow("s3CachePrefix")}
                                        {textRow("sqsQueueUrl")}
                                        {textRow("sqsRegion")}
                                    </Section>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}

export default Tenants;
